import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import {
  reviewEvolution,
  trackContextualAIEvent,
  updateContextualAnalysisStatus,
} from "../../services/contextualAiApi";


const STATUS_LABELS = {
  generated: "Aguardando conferência",
  accepted: "Revisado pela profissional",
  dismissed: "Descartado",
  stale: "Revisão desatualizada",
};


export default function EvolutionAIReviewCard({
  patientId,
  evolutionId,
  existingAnalysis,
  onAnalysisChange,
  onClose,
  showSuccess,
  showError,
}) {
  const [analysis, setAnalysis] = useState(existingAnalysis || null);
  const [loading, setLoading] = useState(false);
  const shownTracked = useRef(false);

  useEffect(() => setAnalysis(existingAnalysis || null), [existingAnalysis]);

  useEffect(() => {
    if (shownTracked.current) return;
    shownTracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      module: "evolution",
      action: "documentation_review",
      metadata: { context_type: "evolution" },
    });
  }, []);

  const status = analysis?.status || "generated";
  const payload = analysis?.payload || {};
  const needsGeneration = !analysis || status === "stale" || status === "dismissed";

  const publish = (next) => {
    setAnalysis(next);
    onAnalysisChange?.(next);
  };

  const runReview = async () => {
    if (loading) return;
    setLoading(true);
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      module: "evolution",
      action: "documentation_review",
      metadata: { context_type: "evolution" },
    });
    try {
      const result = await reviewEvolution({ patientId, evolutionId });
      publish(result);
      trackContextualAIEvent("ai_context_generation_success", {
        module: "evolution",
        action: "documentation_review",
        outcome: result?.payload?.generation_mode || "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result?.id,
        metadata: { context_type: "evolution", status: result?.status },
      });
      showSuccess?.("Revisão documental concluída.");
    } catch (error) {
      trackContextualAIEvent("ai_context_generation_failed", {
        module: "evolution",
        action: "documentation_review",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: {
          context_type: "evolution",
          error_code: String(error?.response?.status || "request_failed"),
        },
      });
      showError?.(
        error?.response?.data?.detail || "Não foi possível revisar esta evolução agora."
      );
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    if (!analysis?.id || loading) return;
    setLoading(true);
    try {
      const updated = await updateContextualAnalysisStatus(analysis.id, nextStatus);
      publish(updated);
      trackContextualAIEvent(
        nextStatus === "accepted"
          ? "ai_context_result_accepted"
          : "ai_context_result_dismissed",
        {
          module: "evolution",
          action: "documentation_review",
          analysis_id: analysis.id,
          metadata: { context_type: "evolution", status: nextStatus },
        }
      );
      showSuccess?.(
        nextStatus === "accepted" ? "Revisão documental confirmada." : "Sugestão descartada."
      );
    } catch (error) {
      showError?.(error?.response?.data?.detail || "Não foi possível atualizar a revisão.");
    } finally {
      setLoading(false);
    }
  };

  if (needsGeneration) {
    return (
      <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">
                {status === "stale"
                  ? "A evolução mudou desde a última revisão"
                  : "Verifique clareza e completude com IA"}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                A IA aponta informações que podem precisar de confirmação, sem alterar o registro.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border px-3 py-2 text-sm hover:bg-white">
              Fechar
            </button>
            <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {status === "stale" ? "Revisar novamente" : "Revisar registro"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-purple-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Revisão documental da evolução</p>
            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status === "accepted" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}>
              {STATUS_LABELS[status] || STATUS_LABELS.generated}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Revisar novamente
          </button>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Fechar revisão">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{payload.summary}</p>

      {Array.isArray(payload.documented_items) && payload.documented_items.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-800">Informações identificadas</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {payload.documented_items.map((item, index) => <li key={index}>✓ {item}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(payload.confirmation_items) && payload.confirmation_items.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-800">Pontos para confirmar</p>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
            {payload.confirmation_items.map((item, index) => <li key={index}>• {item}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(payload.writing_alerts) && payload.writing_alerts.map((item, index) => (
        <div key={index} className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {item}
        </div>
      ))}

      {payload.ai_complement && (
        <div className="mt-4 rounded-lg bg-purple-50 p-3">
          <p className="text-sm font-semibold text-purple-900">Complemento da IA</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-purple-900">{payload.ai_complement}</p>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-500">{payload.disclaimer}</p>

      {status === "generated" && (
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t pt-3">
          <button type="button" onClick={() => changeStatus("dismissed")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <X className="h-4 w-4" /> Descartar
          </button>
          <button type="button" onClick={() => changeStatus("accepted")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-60">
            <Check className="h-4 w-4" /> Marcar como revisado
          </button>
        </div>
      )}
    </div>
  );
}
